import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { postLogDetails } from "../../../Services/APIs/appDetails";
import { getReviewReshootPatientDetails } from "../../../Services/APIs/patient";

// Assets
import welcomeTeeth from "../../../assets/illustrations/welcomeTeeth.png";
import toothlensLogo from "../../../assets/illustrations/Toothlens_logo.png";

// Constants
const COMPANY_BUCKET_MAP = {
  "ab": "ab",
  "tl-star": "starhealth",
  "care": "care",
  "plum": "pluminsurance"
};

const VALID_CAMERA_SIDES = ["front", "back"];

// Keys to preserve in localStorage during cleanup
const PRESERVED_KEYS = [
  "usertpi", 
  "cameraSide", 
  "company", 
  "companyBucket", 
  "scanType", 
  "isAutomated",
  "uid"
];

const ReviewLandingPage = () => {
  const { company = "tl-star", cameraside = "front", uid } = useParams();
  const navigate = useNavigate();
  const { images } = useSelector((state) => state.fullScan);
  
  // State for review status and loading
  const [reviewStatus, setReviewStatus] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [activeCaptureSteps, setActiveCaptureSteps] = useState(null);

  // Resolve company and camera side with fallbacks
  const resolvedCompany = COMPANY_BUCKET_MAP.hasOwnProperty(company) ? company : "tl-star";
  const resolvedCameraSide = VALID_CAMERA_SIDES.includes(cameraside) ? cameraside : "front";
  const bucket = COMPANY_BUCKET_MAP[resolvedCompany];

  // Clean localStorage on component mount but preserve essential data
  const cleanLocalStorageWithPreservation = useCallback(() => {
    // Backup preserved values
    const backup = {};
    PRESERVED_KEYS.forEach(key => {
      const value = localStorage.getItem(key);
      if (value !== null) {
        backup[key] = value;
      }
    });

    // Clear localStorage
    localStorage.clear();

    // Restore preserved values
    Object.entries(backup).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
    
    // Set essential app state
    localStorage.setItem("isAutomated", "true");
    localStorage.setItem("scanType", "tl-star");
    localStorage.setItem("uid", uid);
  }, [uid]);

  // Fetch review details from API
  const fetchReviewDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Set essential user info in localStorage
      localStorage.setItem("usertpi", JSON.stringify({ usertpi: uid }));
      localStorage.setItem("companyBucket", JSON.stringify({ companyBucket: "toothlens" }));
      localStorage.setItem("cameraSide", resolvedCameraSide);
      localStorage.setItem("company", resolvedCompany);
      
      // Fetch review details from API
      const reviewResponse = await getReviewReshootPatientDetails();
      console.log("Review data:", reviewResponse.data.data);
      
      if (reviewResponse.data && reviewResponse.data.data) {
        const reviewData = reviewResponse.data.data;
        
        // Update review status in state and localStorage
        setReviewStatus(reviewData.review_status || "");
        localStorage.setItem("review_status", reviewData.review_status || "");
        
        // Create capture steps object
        const captureSteps = {
          front: reviewData.is_front_teeth_selected,
          left: reviewData.is_left_side_selected,
          right: reviewData.is_right_side_selected,
          lower: reviewData.is_lower_jaw_selected,
          upper: reviewData.is_upper_jaw_selected,
          face: reviewData.is_face_selected,
          aadhar_card: reviewData.is_aadhar_card_selected,
          pan_card: reviewData.is_pan_card_selected
        };
        
        // Update state and localStorage
        setActiveCaptureSteps(captureSteps);
        localStorage.setItem("activeCaptureSteps", JSON.stringify(captureSteps));
      }
      
      // Log user visit
      postLogDetails({ 
        uid, 
        app: `${resolvedCompany} of side ${resolvedCameraSide}`, 
        payLoad: `visited review landing page` 
      });
    } catch (error) {
      console.error("Error fetching review details:", error);
      // Set default status in case of error
      setReviewStatus("");
    } finally {
      setIsLoading(false);
    }
  }, [uid, bucket, resolvedCameraSide, resolvedCompany]);

  // Initialize component
  useEffect(() => {
    // First clean localStorage while preserving essential data
    cleanLocalStorageWithPreservation();
    
    // Then fetch review details
    fetchReviewDetails();
  }, [cleanLocalStorageWithPreservation, fetchReviewDetails]);

  // Handle starting a new scan
  const handleNewScan = () => {
    if (activeCaptureSteps && (activeCaptureSteps.aadhar_card || activeCaptureSteps.pan_card)) {
      navigate(`/review/${uid}/${company}/${cameraside}/file-upload`);
    } else {
      navigate(`/review/${uid}/${company}/${cameraside}/review-scan`);
    }
  };

  return (
    <div className="h-full">
      <div className="h-2/5">
        <img
          src={welcomeTeeth}
          alt="welcome teeth"
          className="saturation h-full w-full object-contain"
        />
      </div>
      
      <div className="h-3/5 relative flex flex-col">
        <div className="flex flex-col flex-grow">
          <div className="flex flex-col items-center flex-grow px-5">
            <h5 className="text-2xl font-medium text-center mb-3">
              Instant Dental Health Scan
            </h5>
            
            <div className="w-full flex flex-col justify-center flex-grow py-2">
              {isLoading ? (
                // Loading state
                <div className="flex justify-center items-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                  <span className="ml-2">Loading scan details...</span>
                </div>
              ) : (
                // Content based on review status
                (reviewStatus.trim().toLowerCase() === "retake") ? (
                  <button
                    onClick={handleNewScan}
                    className="bg-black rounded text-white w-full h-11 font-semibold text-base"
                  >
                    Take Review Scan
                  </button>
                ) : (
                  <div>
                    <p className="text-lg text-green-600 font-medium text-center my-3">
                      You have successfully completed your scan
                    </p>
                    <button
                      className="bg-black rounded text-white w-full h-11 font-semibold text-base"
                      disabled
                    >
                      Scan Completed
                    </button>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        <div className="w-full px-4 mb-4">
          <p className="flex items-center justify-center">
            <span className="opacity-60">Powered by</span>
            <img
              src={toothlensLogo}
              alt="ToothLens Logo"
              className="ml-2 w-24"
            />
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReviewLandingPage;